import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, Check, ChevronLeft, Cpu, Shield, Monitor, Wifi, Zap, Layers, ShoppingBag, Bookmark } from 'lucide-react';
import { useRegion } from '../../contexts/RegionContext';
import { useBag, setPendingBagItem } from '../../contexts/BagContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSaves } from '../../contexts/SavesContext';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const BuyArkPro = () => {
  const { region, formatPrice } = useRegion();
   const { addItem, processPendingItem } = useBag();
  const { isAuthenticated, isLoading } = useAuth();
  const { toggleSave, isSaved } = useSaves();
  const navigate = useNavigate();
  const [selectedChip, setSelectedChip] = useState('core-pro');
  const [selectedMemory, setSelectedMemory] = useState('64gb');
  const [selectedStorage, setSelectedStorage] = useState('1tb');
  const [selectedConnectivity, setSelectedConnectivity] = useState('10gbe');
  const [selectedProjection, setSelectedProjection] = useState('dual');
  const [currentImage, setCurrentImage] = useState(0);
  const [showAddedConfirm, setShowAddedConfirm] = useState(false);

  // Auto-add pending item after auth redirect back
  useEffect(() => {
    if (isAuthenticated) {
      const wasProcessed = processPendingItem();
      if (wasProcessed) {
        setShowAddedConfirm(true);
        setTimeout(() => setShowAddedConfirm(false), 4000);
      }
    }
  }, [isAuthenticated, processPendingItem]);

  const basePrice = region.arkPro.min;
  const range = region.arkPro.max - region.arkPro.min;
  const priceAdds = {
    chip: { 'core-pro': 0, 'core-ultra': Math.round(range * 0.25) },
    memory: { '64gb': 0, '128gb': Math.round(range * 0.15) },
    storage: { '1tb': 0, '2tb': Math.round(range * 0.12), '4tb': Math.round(range * 0.22) },
    connectivity: { '10gbe': 0, '25gbe': Math.round(range * 0.10) },
    projection: { 'dual': 0, 'quad': Math.round(range * 0.16) },
  };

  const totalPrice = basePrice
    + priceAdds.chip[selectedChip]
    + priceAdds.memory[selectedMemory]
    + priceAdds.storage[selectedStorage]
    + priceAdds.connectivity[selectedConnectivity]
    + priceAdds.projection[selectedProjection];

  const configs = {
    chip: [
      { id: 'core-pro', label: 'T.A.E.L.S Core Pro', detail: '36 trillion operations per second', note: 'Included', addPrice: 0 },
      { id: 'core-ultra', label: 'T.A.E.L.S Core Ultra', detail: '72 trillion operations per second', note: 'Maximum performance', addPrice: priceAdds.chip['core-ultra'] },
    ],
    memory: [
      { id: '64gb', label: '64GB Unified Memory', note: 'Included', addPrice: 0 },
      { id: '128gb', label: '128GB Unified Memory', note: 'For maximum parallel exhibits', addPrice: priceAdds.memory['128gb'] },
    ],
    storage: [
      { id: '1tb', label: '1TB SSD', note: 'Included', addPrice: 0 },
      { id: '2tb', label: '2TB SSD', note: 'Full museum library', addPrice: priceAdds.storage['2tb'] },
      { id: '4tb', label: '4TB SSD', note: 'Enterprise archive', addPrice: priceAdds.storage['4tb'] },
    ],
    connectivity: [
      { id: '10gbe', label: '10 Gigabit Ethernet + Wi-Fi 6E', note: 'Included', addPrice: 0 },
      { id: '25gbe', label: '25 Gigabit Ethernet + Wi-Fi 7', note: 'Data-center grade', addPrice: priceAdds.connectivity['25gbe'] },
    ],
    projection: [
      { id: 'dual', label: 'Dual 8K HDMI 2.1 Output', note: 'Included', addPrice: 0 },
      { id: 'quad', label: 'Quad 8K Output (360° Panoramic)', note: 'Full immersive canvas', addPrice: priceAdds.projection['quad'] },
    ],
  };

  const images = [
    process.env.PUBLIC_URL + '/ark-pro-product.png',
    process.env.PUBLIC_URL + '/fozzil-intelligence-core.png',
  ];

  const handleAddToBag = () => {
    const bagItem = {
      id: 'fozzil-ark-pro',
      name: 'FOZZIL ARK Pro',
      tagline: 'Pro performance. Pro possibilities.',
      image: images[0],
      config: [
        { label: 'Chip', value: configs.chip.find(c => c.id === selectedChip)?.label },
        { label: 'Memory', value: configs.memory.find(m => m.id === selectedMemory)?.label },
        { label: 'Storage', value: configs.storage.find(s => s.id === selectedStorage)?.label },
        { label: 'Projection', value: configs.projection.find(p => p.id === selectedProjection)?.label },
        { label: 'Connectivity', value: configs.connectivity.find(c => c.id === selectedConnectivity)?.label },
      ],
      price: totalPrice,
    };

    if (!isAuthenticated) {
      if (isLoading) return; // Wait if still checking session
      setPendingBagItem(bagItem);
      navigate('/auth', { state: { from: { pathname: '/shop/buy-ark/ark-pro' } } });
      return;
    }
    addItem(bagItem);
    setShowAddedConfirm(true);
    setTimeout(() => setShowAddedConfirm(false), 4000);
  };

  const handleToggleSave = () => {
    if (!isAuthenticated) {
      navigate('/auth', { state: { from: { pathname: '/shop/buy-ark/ark-pro' } } });
      return;
    }
    
    const product = {
      id: 'fozzil-ark-pro',
      name: 'FOZZIL ARK Pro',
      tagline: 'Pro performance. Pro possibilities.',
      image: images[0],
      link: '/shop/buy-ark/ark-pro',
      price: formatPrice(totalPrice),
    };
    
    toggleSave(product);
  };

  const ConfigSection = ({ title, options, selected, onSelect }) => (
    <div className="mb-8">
      <h3 className="text-sm font-bold uppercase tracking-wide mb-3">{title}</h3>
      <div className="flex flex-col gap-3">
        {options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onSelect(opt.id)}
            className={`w-full text-left rounded-[32px] p-4 border-2 transition-all duration-200 cursor-pointer bg-transparent ${
              selected === opt.id
                ? 'border-[#065DC0] bg-[#065DC0]/[0.06]'
                : 'border-black/[0.08] dark:border-white/[0.08] hover:border-black/[0.18] dark:hover:border-white/[0.18]'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">{opt.label}</p>
                {opt.detail && <p className="text-xs text-[#6e6e73] dark:text-[#a1a1a6] mt-0.5">{opt.detail}</p>}
                <p className="text-xs text-[#86868b] dark:text-[#6e6e73] mt-0.5">{opt.note}</p>
              </div>
              <div className="flex items-center gap-2">
                {opt.addPrice > 0 && (
                  <span className="text-xs text-[#6e6e73] dark:text-[#a1a1a6] font-medium">+{formatPrice(opt.addPrice)}</span>
                )}
                {selected === opt.id && <Check size={18} className="text-[#065DC0] shrink-0" />}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const checkoutConfig = [
    ['Chip', configs.chip.find(c => c.id === selectedChip)?.label],
    ['Memory', configs.memory.find(m => m.id === selectedMemory)?.label],
    ['Storage', configs.storage.find(s => s.id === selectedStorage)?.label],
    ['Projection', configs.projection.find(p => p.id === selectedProjection)?.label],
    ['Connectivity', configs.connectivity.find(c => c.id === selectedConnectivity)?.label],
  ];

  return (
    <div className="store-page bg-white dark:bg-black text-[#1d1d1f] dark:text-[#f5f5f7] transition-colors duration-400 min-h-screen">
      {/* Added to Bag Toast */}
      <AnimatePresence>
        {showAddedConfirm && (
          <motion.div
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-[3000] bg-[#1d1d1f] dark:bg-white text-white dark:text-[#1d1d1f] px-6 py-4 rounded-[32px] shadow-2xl flex items-center gap-4 max-w-[420px] w-[90vw]"
          >
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center shrink-0">
              <Check size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Added to Bag</p>
              <p className="text-xs opacity-70 truncate">FOZZIL ARK Pro — {formatPrice(totalPrice)}</p>
            </div>
            <button
              onClick={() => { setShowAddedConfirm(false); navigate('/shop/bag'); }}
              className="px-4 py-2 rounded-full bg-[#065DC0] text-white text-xs font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all shrink-0"
            >
              Review Bag
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Breadcrumb */}
      <div className="max-w-[1120px] mx-auto px-6 py-4">
        <Link to="/shop/buy-ark" className="inline-flex items-center gap-1 text-[#065DC0] text-sm font-medium hover:underline">
          <ChevronLeft size={16} /> Back to Shop ARK
        </Link>
      </div>

      {/* Product Section */}
      <div className="max-w-[1120px] mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left: Image Gallery */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="lg:sticky lg:top-[120px] self-start">
            <div className="bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-[32px] border border-black/[0.08] dark:border-white/[0.08] flex items-center justify-center aspect-square overflow-hidden transition-colors duration-400">
              <img src={images[currentImage]} alt="FOZZIL ARK Pro" className="w-full h-full object-contain transition-all duration-500" />
            </div>
            <div className="flex items-center justify-center gap-3 mt-4">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImage(i)}
                  className={`w-16 h-16 rounded-[32px] border overflow-hidden cursor-pointer transition-all duration-200 p-1 ${
                    currentImage === i
                      ? 'border-[#065DC0] shadow-[0_0_0_2px_rgba(6,93,192,0.3)]'
                      : 'border-black/[0.08] dark:border-white/[0.08] hover:border-black/[0.18] dark:hover:border-white/[0.18]'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>

            {/* Pro Badge */}
            <div className="mt-6 bg-white dark:bg-[#1c1c1e] border border-black/[0.08] dark:border-white/[0.08] rounded-[32px] p-5 flex items-center gap-4 transition-colors duration-400">
              <div className="w-12 h-12 rounded-[32px] bg-[#065DC0]/10 flex items-center justify-center shrink-0">
                <Zap size={24} className="text-[#065DC0]" />
              </div>
              <div>
                <p className="text-sm font-bold">Built for professionals</p>
                <p className="text-xs text-[#6e6e73] dark:text-[#a1a1a6] mt-0.5">ARK Pro is designed for large-scale museum installations and enterprise deployments.</p>
              </div>
            </div>
          </motion.div>

          {/* Right: Configuration */}
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="py-4">
            <span className="text-[11px] font-bold text-[#bf4800] uppercase tracking-wide">NEW</span>
            <h1 className="text-[clamp(32px,4vw,48px)] font-bold tracking-tight leading-tight mt-1 mb-2">FOZZIL ARK Pro</h1>
            <p className="text-lg text-[#6e6e73] dark:text-[#a1a1a6] mb-2">Pro performance. Pro possibilities.</p>
            <p className="text-2xl font-semibold mb-1">From {formatPrice(region.arkPro.min)}</p>
            <p className="text-sm text-[#86868b] dark:text-[#6e6e73] mb-8">{formatPrice(region.arkPro.min)} – {formatPrice(region.arkPro.max)}</p>

            <ConfigSection title="Chip" options={configs.chip} selected={selectedChip} onSelect={setSelectedChip} />
            <ConfigSection title="Memory" options={configs.memory} selected={selectedMemory} onSelect={setSelectedMemory} />
            <ConfigSection title="Storage" options={configs.storage} selected={selectedStorage} onSelect={setSelectedStorage} />
            <ConfigSection title="Projection Output" options={configs.projection} selected={selectedProjection} onSelect={setSelectedProjection} />
            <ConfigSection title="Connectivity" options={configs.connectivity} selected={selectedConnectivity} onSelect={setSelectedConnectivity} />

            {/* Summary */}
            <div className="bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-[32px] border border-black/[0.08] dark:border-white/[0.08] p-6 mb-8 transition-colors duration-400">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold">Your FOZZIL ARK Pro</h3>
                <span className="text-lg font-bold text-[#065DC0]">{formatPrice(totalPrice)}</span>
              </div>
              <div className="flex flex-col gap-2.5 text-sm">
                {checkoutConfig.map(([label, value]) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-[#6e6e73] dark:text-[#a1a1a6]">{label}</span>
                    <span className="font-medium">{value}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-3 mt-1 border-t border-black/[0.08] dark:border-white/[0.08]">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-[#065DC0]">{formatPrice(totalPrice)}</span>
                </div>
              </div>
            </div>

            {/* CTA — Add to Bag & Save */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <button
                  onClick={handleToggleSave}
                  className={`flex-1 px-4 py-3.5 rounded-full bg-transparent text-[#065DC0] text-sm font-semibold border border-[#065DC0] text-center inline-flex items-center justify-center gap-2 hover:bg-[#065DC0] hover:text-white transition-all cursor-pointer ${isSaved('fozzil-ark-pro') ? 'bg-[#065DC0] !text-white' : ''}`}
                >
                  <Bookmark size={18} fill={isSaved('fozzil-ark-pro') ? 'currentColor' : 'none'} /> {isSaved('fozzil-ark-pro') ? 'Saved' : 'Save'}
                </button>
                <button
                  onClick={handleAddToBag}
                  className="flex-[2] px-6 py-3.5 rounded-full bg-[#065DC0] text-white text-sm font-semibold text-center inline-flex items-center justify-center gap-2 hover:bg-[#0077ed] hover:scale-[1.01] transition-all cursor-pointer border-none"
                >
                  <ShoppingBag size={18} /> Add to Bag — {formatPrice(totalPrice)}
                </button>
              </div>
              <Link to="/fozzil-ark" target="_blank" rel="noopener noreferrer" className="w-full px-8 py-3.5 mx-auto rounded-full bg-transparent text-[#065DC0] text-sm font-semibold border border-[#065DC0] text-center inline-flex items-center justify-center gap-2 hover:bg-[#065DC0] hover:text-white transition-all">
                Learn more about ARK Pro
              </Link>
            </div>

            {/* What's Included */}
            <div className="mt-10 border-t border-black/[0.08] dark:border-white/[0.08] pt-8">
              <h3 className="text-base font-bold mb-5">What's included</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: <Shield size={20} />, label: 'Advanced S.H.A.D.E. Vision' },
                  { icon: <Cpu size={20} />, label: 'Intelligence Core Pro' },
                  { icon: <Monitor size={20} />, label: 'Multi 8K HDMI 2.1' },
                  { icon: <Wifi size={20} />, label: '10GbE + Wi-Fi 6E' },
                  { icon: <Zap size={20} />, label: 'Living Ancestor Engine' },
                  { icon: <Shield size={20} />, label: 'Dual Neural Vault' },
                  { icon: <Layers size={20} />, label: '360° Projection Support' },
                  { icon: <Shield size={20} />, label: 'Enterprise Management' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2.5">
                    <div className="text-[#065DC0]">{item.icon}</div>
                    <span className="text-sm text-[#6e6e73] dark:text-[#a1a1a6] font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Compare */}
            <div className="mt-10 border-t border-black/[0.08] dark:border-white/[0.08] pt-8">
              <p className="text-sm text-[#6e6e73] dark:text-[#a1a1a6] mb-3">Not sure if Pro is right for you?</p>
              <Link to="/shop" className="text-[#065DC0] text-sm font-medium inline-flex items-center gap-1 hover:underline">
                Compare all ARK models <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BuyArkPro;
